import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import ShopfrontOffline from "../../Common/Misc/ShopfrontOffline";
import { UILayerConsumer } from "../../Common/Misc/UILayerConsumer";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { type RootState } from "../../Reducers/ShopfrontReducers";
import { getOption, setOption } from "../../Utilities/Platform/Options/Options";
import Request from "../../Utilities/Platform/Request";
import SaleReceiptPrint from "../SellScreen/Receipt/SaleReceiptPrint";
import { LoginForm } from "./Form/LoginForm";
import LoginNews from "./News/LoginNews";
import { type LoginUserType } from "./User/LoginUser";
import LoginUserList from "./User/LoginUserList";

const connector = connect((state: RootState) => ({
    mask        : false,
    touchNumbers: false,
    previousSale: state.sellscreen.previousSale,
    first       : state.information.firstVisit,
}));

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
    mode: "type" | "click";
    loginUser: (username: string) => Promise<void>;
    loginOffline: (username: string, forced: boolean) => Promise<void>;
    requirePassword: boolean;
    users: Array<LoginUserType>;
    onRequirePasswordChange: (requirePassword: boolean) => void;
}

type State = {
    username: string;
    password: string;
    offlineNoData: boolean;
    token: string;
    timeoutsReceived: number;
};

export class LoginBox extends React.Component<Props, State> {
    public state: State = {
        username        : "",
        password        : "",
        offlineNoData   : false,
        token           : getOption("current_login_token"),
        timeoutsReceived: 0,
    };

    public handleChange = (name: string, value: string, callback?: () => void): void => {
        if(name === "username") {
            if(
                getOption("sign_in_password_first_login") &&
                !getOption("sign_in_require_password")
            ) {
                this.props.onRequirePasswordChange(false);
            }
        }

        this.setState(prevState => ({
            ...prevState,
            [name]: value,
        }), callback);
    };

    public handleLogin = (): Promise<void> => {
        if(this.props.mode === "type" && !this.state.username) {
            if(this.props.requirePassword && !this.state.password) {
                PopupManager.error("Please enter a username & password.");

                return Promise.reject();
            } else {
                PopupManager.error("Please enter a username.");

                return Promise.reject();
            }
        } else if(this.props.requirePassword && !this.state.password) {
            PopupManager.error("Please enter a password.");

            return Promise.reject();
        }

        return Request.post((this.props.requirePassword ? "/login" : "/login-user"), {
            username: this.state.username,
            password: this.state.password,
            token   : this.state.token,
        }, {
            timeout: 3000,
        })
            .then(response => {
                if(response.data.token) {
                    setOption("current_login_token", response.data.token);
                }

                return this.props.loginUser(this.state.username);
            })
            .catch((err) => {
                // Check if timed out
                if(err.code === "ECONNABORTED") {
                    return this.timeout()
                        .then(offline => {
                            if(offline) {
                                return Promise.resolve();
                            } else {
                                return Promise.reject("Login timed out");
                            }
                        });
                } else {
                    this.setState({
                        timeoutsReceived: 0,
                    });
                }

                // Check if offline
                if(
                    !err.response ||
                    err.response.status === 0 ||
                    err.response.status === 540 ||
                    err.response.status === 541
                ) {
                    // Try to login user offline
                    return this.loginOffline()
                        .then(() => { /* no-op */ });
                } else if(err.response.status === 401 && !this.props.requirePassword) {
                    if(!getOption("sign_in_password_first_login")) {
                        PopupManager.info("Your login session has expired, please enter your password.");
                    }

                    this.props.onRequirePasswordChange(true);

                    return Promise.reject("Failed to login");
                } else {
                    return this.invalidLogin();
                }
            });
    };

    public invalidLogin = (): Promise<void> => {
        PopupManager.error("Invalid login details. Please try again.");

        return Promise.reject("Failed to login");
    };

    public handleClock = (): Promise<void> => {
        return Promise.resolve();
    };

    protected loginOffline(forced = false): Promise<boolean> {
        return this.props.loginOffline(this.state.username, forced)
            .then(() => true)
            .catch(async () => {
                const source = await determineDataSource();
                const users = await source.users.all();

                if(users.length === 0) {
                    this.setState({
                        offlineNoData: true,
                    });

                    return true;
                }

                return this.invalidLogin()
                    .then(() => true);
            });
    }

    protected timeout(): Promise<boolean> {
        if(this.state.timeoutsReceived > 2) {
            PopupManager.info("Login timed out, we've logged you in using your offline credentials");

            return this.loginOffline(true);
        } else {
            PopupManager.info("Login timed out, please try again");
            this.setState({
                timeoutsReceived: this.state.timeoutsReceived + 1,
            });
        }

        return Promise.resolve(false);
    }

    public render(): React.ReactNode {
        if(this.state.offlineNoData) {
            return <ShopfrontOffline />;
        }

        let newsBox;

        if(this.props.previousSale && this.props.previousSale.status !== "CANCELLED") {
            newsBox = (
                <SaleReceiptPrint
                    sale={this.props.previousSale}
                    logout
                />
            );
        } else {
            newsBox = <LoginNews />;
        }

        return (
            <UILayerConsumer zIndex={4}>
                <div id="login-box" className={this.props.mode} data-testid="sl-login-box">
                    <div id="login-form">
                        {this.props.mode === "click" ? (
                            <LoginUserList
                                users={this.props.users}
                                requirePassword={this.props.requirePassword}
                                touchNumbers={this.props.touchNumbers}
                                password={this.state.password}
                                username={this.state.username}
                                onChange={this.handleChange}
                                onLogin={this.handleLogin}
                                onClock={this.handleClock}
                            />
                        ) : (
                            <LoginForm
                                showUsername
                                requirePassword={this.props.requirePassword}
                                touchNumbers={this.props.touchNumbers}
                                password={this.state.password}
                                username={this.state.username}
                                onChange={this.handleChange}
                                onLogin={this.handleLogin}
                                onClock={this.handleClock}
                            />
                        )}
                    </div>
                    <div id="login-news">
                        {newsBox}
                    </div>
                </div>
            </UILayerConsumer>
        );
    }
}

export default connector(LoginBox);
